import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { FormikControl, checkResponses } from '../../../../components/form';
import { getDataArray, exportToCSV } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';
import BookListTemplateModal from './BookListTemplateModal';

import {
    fetchBookListTemplatesAll,
    fetchBooksAll,
    createBookListTemplate,
    updateBookListTemplate,
    deleteBookListTemplate
} from '../../../../actions';

const pageTitle = 'Book List Templates';

function BookListTemplates(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [books, setBooks] = useState([]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const { fetchBookListTemplatesAll, fetchBooksAll, createBookListTemplate, updateBookListTemplate, deleteBookListTemplate } = props;

    const handleOpenModal = (mode, template = null) => {
        setModalMode(mode);
        setSelectedTemplate(template);
        setShowModal(true);
    };

    const handleCloseModal = (shouldRefresh = false) => {
        setShowModal(false);
        setSelectedTemplate(null);
        if (shouldRefresh) {
            refreshData();
        }
    };

    const filterTemplates = useCallback((unfilteredTemplates, filterQuery) => {
        if (!filterQuery || filterQuery.trim() === '') {
            setFilteredTemplates(unfilteredTemplates);
            return;
        }
        const query = filterQuery.toLowerCase().trim();
        const filtered = unfilteredTemplates.filter(t => {
            return (t.name && t.name.toLowerCase().includes(query)) ||
                (t.description && t.description.toLowerCase().includes(query));
        });
        setFilteredTemplates(filtered);
    }, []);

    const handleExportCSV = () => {
        const csvData = filteredTemplates.map(t => ({
            'Template Name': t.name || '',
            'Description': t.description || '',
            'Books Count': t.book_count || 0
        }));
        exportToCSV(csvData, 'Book_List_Templates_Export');
    };

    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading) return;
            if (mounted.current) setLoading(true);

            try {
                const [templatesRes, booksRes] = await Promise.all([
                    fetchBookListTemplatesAll(),
                    fetchBooksAll()
                ]);
                const isApiError = checkResponses(templatesRes, booksRes);
                if (isApiError) {
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                // Backend returns { templates: [...], templateItems: [...] }
                const templatesData = templatesRes.data || {};
                const newTemplates = Array.isArray(templatesData.templates) ? templatesData.templates : getDataArray(templatesRes);
                const templateItems = Array.isArray(templatesData.templateItems) ? templatesData.templateItems : [];

                // Attach items to each template
                const templatesWithItems = newTemplates.map(template => {
                    const items = templateItems
                        .filter(item => parseInt(item.book_list_template_id) === parseInt(template.id))
                        .sort((a, b) => (parseInt(a.sort_order) || 0) - (parseInt(b.sort_order) || 0))
                        .map(item => ({
                            book_id: parseInt(item.book_id),
                            sort_order: parseInt(item.sort_order) || 0
                        }));
                    return {
                        ...template,
                        description: template.description || template.notes || '',
                        notes: template.notes || template.description || '',
                        book_count: items.length,
                        items: items
                    };
                });

                // Extract books - try multiple response formats
                let newBooks = [];
                if (booksRes?.data?.books) {
                    newBooks = booksRes.data.books;
                } else {
                    newBooks = getDataArray(booksRes);
                }

                const { filterQuery } = formRef.current?.values || { filterQuery: '' };

                if (mounted.current) {
                    setTemplates(templatesWithItems);
                    setBooks(newBooks);
                    filterTemplates(templatesWithItems, filterQuery);
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[BOOK LIST TEMPLATES] Error:', error);
                if (mounted.current) {
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchBookListTemplatesAll, fetchBooksAll, filterTemplates]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getColumns = () => [
        {
            dataField: 'name',
            text: 'Template Name',
            sort: true,
            formatter: (row) => (
                <span className="font-semibold">{row?.name || 'Unnamed'}</span>
            )
        },
        {
            dataField: 'description',
            text: 'Description',
            formatter: (row) => (
                <span className="text-sm">{row?.description || '-'}</span>
            )
        },
        {
            dataField: 'book_count',
            text: 'Books',
            sort: true,
            formatter: (row) => {
                // Try to get book count from items
                let count = 0;
                if (row?.items) {
                    try {
                        const items = typeof row.items === 'string' ? JSON.parse(row.items) : row.items;
                        count = Array.isArray(items) ? items.length : 0;
                    } catch (e) {
                        count = 0;
                    }
                }
                return <span>{count}</span>;
            }
        },
        {
            dataField: 'actions',
            text: 'Actions',
            formatter: (row) => (
                <div className="flex flex-row gap-x-2">
                    <TooltipWrapper tooltipText="Edit template">
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                            onClick={() => handleOpenModal('edit', row)}
                        >
                            <SVGIcon fileName="pencil" />
                        </Button>
                    </TooltipWrapper>
                    <TooltipWrapper tooltipText="Delete template">
                        <Button
                            color="lte-mpLRed"
                            variant="icon"
                            onClick={() => handleOpenModal('delete', row)}
                        >
                            <SVGIcon fileName="trash" />
                        </Button>
                    </TooltipWrapper>
                </div>
            )
        }
    ];

    const defaultSorted = { dataField: 'name', order: 'asc' };

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{ filterQuery: '' }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <h2>{pageTitle}</h2>
                            <br />
                            <div className="flex flex-row gap-x-4 items-center mb-4 flex-wrap">
                                <div className="grid grid-cols-1 gap-y-2 flex-1 min-w-[200px]">
                                    <FormikControl
                                        id="book-list-templates-searchQuery"
                                        name="filterQuery"
                                        placeholder="Search templates..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterTemplates(templates, e.target.value);
                                        }}
                                    />
                                </div>
                                <Button
                                    color="lte-mpTeal"
                                    onClick={() => handleOpenModal('create')}
                                >
                                    + Add Template
                                </Button>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={handleExportCSV}
                                >
                                    Export to CSV
                                </Button>
                            </div>
                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded &&
                                <Table
                                    tableName="bookListTemplates"
                                    data={filteredTemplates}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No book list templates found"
                                />
                            }
                        </form>
                    )}
                </Formik>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />

            {showModal && (
                <BookListTemplateModal
                    key={`template-modal-${modalMode}-${selectedTemplate?.id || 'new'}`}
                    show={showModal}
                    mode={modalMode}
                    template={selectedTemplate}
                    books={books}
                    onClose={handleCloseModal}
                    createBookListTemplate={createBookListTemplate}
                    updateBookListTemplate={updateBookListTemplate}
                    deleteBookListTemplate={deleteBookListTemplate}
                />
            )}
        </div>
    );
};

export default connect(null, {
    fetchBookListTemplatesAll,
    fetchBooksAll,
    createBookListTemplate,
    updateBookListTemplate,
    deleteBookListTemplate
})(BookListTemplates);
